import { useCallback, useEffect, useState } from "react";
import { useDeleteAsk, deletionRequested } from "@/lib/deleteApproval";
import { nameProblem, autoFixName } from "@/lib/names";
import GhlPipelineSyncCard from "@/components/GhlPipelineSyncCard";
import WebsiteIntakeCard from "@/components/WebsiteIntakeCard";
import WalkinQrCard from "@/components/WalkinQrCard";
import IncentiveCountries from "@/components/IncentiveCountries";
import {
  getSlabHistory,
  listScripts, createScript, uploadScript, deleteScript,
  getRenewalSettings, setRenewalSettings,
  setFacilityTax,
  setFacilityAccountability,
  getProgrammeCatalog, setProgrammeCatalog, listOverrideLog,
  getSaleTypes, setSaleTypes, getRevenueItems, setRevenueItems, getBillingDetails, sendTestBill, setBillingDetails,
  getObjections, setObjections,
  getPaymentFees, setPaymentFees,
  getAuditExercises, setAuditExercises,
  listFacilities, createFacility, updateFacility, deactivateFacility,
  listFacilityUsers, detachUserFromFacility, deleteFacilityPermanent,
  listSessionSchedules, saveSessionSchedules,
} from "@/lib/api";
import { resetPaymentModesCache } from "@/lib/paymentModes";
import { useAuth } from "@/context/AuthContext";
import { atLeast } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Plus, Trash2, Save, History, FileText, UploadCloud, Loader2, Lock, Copy,
  UserRound, BellRing, Pencil, Package, ShieldAlert, Tag, CreditCard, Dumbbell, Building2, Globe2, Clock,
  ReceiptText, Percent, Bell, CalendarClock, QrCode, Wallet, Fingerprint, ListChecks, ShoppingBag,
  MessageSquareWarning, Workflow, Globe, ClipboardCheck, ChevronLeft, ChevronRight,
} from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { numberLocale } from "@/lib/money";

const ROSTER_SKILL_OPTS = [
  { value: "sales", label: "Sales" },
  { value: "accountability", label: "Accountability" },
  { value: "lead_nurture", label: "Lead Nurture" },
  { value: "SPT", label: "SPT" },
  { value: "PT", label: "PT" },
];
const SKILL_LABEL = Object.fromEntries(ROSTER_SKILL_OPTS.map((x) => [x.value, x.label]));

// Stable client-side row id for editable-list keys (so React state survives add/remove/reorder).
let _ridCounter = 0;
const _newRid = () => `r_${Date.now()}_${++_ridCounter}`;
const withRids = (rows) => (rows || []).map((r) => (r && r._rid ? r : { ...r, _rid: _newRid() }));
const stripRids = (rows) => (rows || []).map(({ _rid, ...rest }) => rest);

// Settings: a list of topics, grouped; clicking one opens its details (?topic=…, with a way back).
const TOPIC_GROUPS = ["Gym", "Sales & pricing", "Leads & GoHighLevel", "Coaching", "Incentives & scripts"];

function useTopics() {
  const { user, effectiveRole } = useAuth();
  const role = effectiveRole || user?.role;
  const manager = atLeast(user?.role, "manager");
  const pricing = atLeast(user?.role, "admin") || !!user?.accounts_access || !!user?.accounts_head;
  return [
    { id: "billing", group: "Gym", icon: ReceiptText, title: "Billing details", desc: "The gym's legal name, address, tax number and the GoHighLevel account bills are emailed from.",
      render: () => <BillingDetailsCard canEdit={manager} /> },
    { id: "tax", group: "Gym", icon: Percent, title: "Tax on sales", desc: "GST / VAT on sales for this gym.", render: () => <TaxationSettings /> },
    { id: "renewals", group: "Gym", icon: Bell, title: "Renewal reminders", desc: "When members are flagged as due for renewal.", render: () => <RenewalSettings /> },
    { id: "sessions", group: "Gym", icon: CalendarClock, title: "Session schedules", desc: "Weekly class slots used by the check-in tablet.",
      render: () => <SessionScheduleCard canEdit={manager} /> },
    ...(atLeast(role, "manager") ? [
      { id: "questionnaire", group: "Gym", icon: QrCode, title: "Pre-sale questionnaire QR", desc: "The QR members scan at the front desk before a consult.", render: () => <WalkinQrCard /> },
      { id: "petty-cash", group: "Gym", icon: Wallet, title: "Petty cash", desc: "The gym's cash float: spends, top-ups and the balance.", to: "/petty-cash" },
      { id: "merch-stock", group: "Gym", icon: Package, title: "Merch stock", desc: "T-shirts and merch on hand, by size.", to: "/merch-stock" },
      { id: "gate", group: "Gym", icon: Fingerprint, title: "Gate entries", desc: "Fingerprint readers at the door and who came in.", to: "/gate" },
    ] : []),
    { id: "programmes", group: "Sales & pricing", icon: Tag, title: "Programmes & prices", desc: pricing ? "Programmes, prices and durations." : "Programmes, prices and durations. Only the accounts team or an admin can change them.",
      render: () => <ProgrammeCatalog canEdit={pricing} /> },
    { id: "sale-types", group: "Sales & pricing", icon: ListChecks, title: "Sale types", desc: "The Type dropdown on All Sales.", render: () => <SaleTypesCatalog canEdit={manager} /> },
    { id: "revenue", group: "Sales & pricing", icon: ShoppingBag, title: "Other revenue", desc: "Merch, events and services sold on All Sales.", render: () => <RevenueItemsCatalog canEdit={manager} /> },
    { id: "fees", group: "Sales & pricing", icon: CreditCard, title: "Payment gateway fees", desc: "Card / UPI fees taken off each payment.", render: () => <PaymentFeesCatalog canEdit={manager} /> },
    { id: "objections", group: "Sales & pricing", icon: MessageSquareWarning, title: "Not-sold reasons", desc: "The objections a rep picks when a consult isn't sold.", render: () => <ObjectionCatalog canEdit={manager} /> },
    { id: "overrides", group: "Sales & pricing", icon: History, title: "Price override log", desc: "Every sale entered at a price different from the catalog.", render: () => <OverrideLogPanel canView={manager} /> },
    { id: "ghl", group: "Leads & GoHighLevel", icon: Workflow, title: "GoHighLevel pipeline sync", desc: "Which pipeline consults and leads sync with.", render: () => <GhlPipelineSyncCard canEdit={manager} /> },
    { id: "website", group: "Leads & GoHighLevel", icon: Globe, title: "Website applications", desc: "How applications from liftr.fit come in.", render: () => <WebsiteIntakeCard canEdit={manager} /> },
    { id: "accountability", group: "Coaching", icon: ClipboardCheck, title: "Accountability & blood tests", desc: "Blood test settings for programmes.", render: () => <AccountabilitySettings /> },
    { id: "audit-exercises", group: "Coaching", icon: Dumbbell, title: "Audit exercises", desc: "The lifts and tests used in member audits.", render: () => <AuditExercisesCatalog canEdit={manager} /> },
    { id: "incentives", group: "Incentives & scripts", icon: Percent, title: "Incentives", desc: "This country's incentive slabs (view only) and their history.", render: () => <IncentivesTopic /> },
    { id: "scripts", group: "Incentives & scripts", icon: FileText, title: "Sales scripts", desc: "The official scripts calls are scored against.", render: () => <ScriptsTopic /> },
  ];
}

export default function Settings() {
  const [params, setParams] = useSearchParams();
  const topics = useTopics();
  const open = topics.find((t) => t.id === params.get("topic") && t.render);
  const go = (id) => { setParams(id ? { topic: id } : {}); window.scrollTo(0, 0); };

  if (open) {
    return (
      <div className="space-y-6" data-testid={`settings-topic-${open.id}`}>
        <div>
          <button type="button" onClick={() => go(null)} className="text-sm text-zinc-400 hover:text-zinc-100 inline-flex items-center gap-1" data-testid="settings-back">
            <ChevronLeft className="h-4 w-4" /> Settings
          </button>
          <h2 className="font-display text-2xl font-extrabold mt-2 tracking-tight flex items-center gap-2"><open.icon className="h-5 w-5 text-blue-400" />{open.title}</h2>
          <p className="text-sm text-zinc-400 mt-1">{open.desc}</p>
        </div>
        {open.render()}
      </div>
    );
  }
  return (
    <div className="space-y-8" data-testid="settings-page">
      <div>
        <div className="eyebrow">Gym settings</div>
        <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Settings</h2>
      </div>
      {TOPIC_GROUPS.map((g) => {
        const list = topics.filter((t) => t.group === g);
        if (!list.length) return null;
        return (
          <section key={g} className="space-y-2">
            <h3 className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">{g}</h3>
            <div className="card-surface divide-y divide-[#1a1a1a] overflow-hidden">
              {list.map((t) => {
                const inner = (
                  <>
                    <span className="h-9 w-9 rounded-lg bg-[#141414] border border-[#222] flex items-center justify-center shrink-0"><t.icon className="h-4 w-4 text-zinc-300" /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-zinc-100">{t.title}</span>
                      <span className="block text-xs text-zinc-500 truncate">{t.desc}</span>
                    </span>
                    <ChevronRight className="h-4 w-4 text-zinc-600 shrink-0" />
                  </>
                );
                const cls = "w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#141414] transition-colors";
                return t.to
                  ? <Link key={t.id} to={t.to} className={cls} data-testid={`settings-link-${t.id}`}>{inner}</Link>
                  : <button key={t.id} type="button" onClick={() => go(t.id)} className={cls} data-testid={`settings-open-${t.id}`}>{inner}</button>;
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function IncentivesTopic() {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  useEffect(() => { getSlabHistory().then(setHistory).catch(() => {}); }, []);
  void user;
  return (
      <section className="space-y-4">
        <div>
          <h3 className="font-display text-xl font-bold">Incentives</h3>
          <div className="text-xs text-zinc-500 mt-1 inline-flex items-center gap-1.5">
            <Lock className="h-3 w-3" /> Set per country in Global Admin → Incentives (lead nurture: Lead Nurture → Script). View-only here.
          </div>
        </div>
        <IncentiveCountries mine fields={["sales", "sales_bonus", "fe_conversion", "nutrition_rate", "blood_test_cost", "leads"]} canEdit={false} />

        <div className="card-surface p-6" data-testid="slab-history-section">
          <div className="flex items-center gap-2 mb-4">
            <History className="h-4 w-4 text-blue-400" />
            <h3 className="font-display text-lg font-bold">Slab change history</h3>
          </div>
          {history.length === 0 ? (
            <div className="text-sm text-zinc-500">No changes recorded yet. First save will start the trail.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-[0.2em] text-zinc-500 border-b border-[#1f1f1f]">
                    <th className="px-3 py-2 font-medium">When</th>
                    <th className="px-3 py-2 font-medium">Changed by</th>
                    <th className="px-3 py-2 font-medium">Sales slabs</th>
                    <th className="px-3 py-2 font-medium">Leads slabs</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h, i) => (
                    <tr key={h.at || `slab-hist-${i}`} className="border-b border-[#1a1a1a] last:border-0 hover:bg-[#161616]" data-testid={`slab-history-row-${i}`}>
                      <td className="px-3 py-3 text-zinc-400 text-xs whitespace-nowrap">{new Date(h.at).toLocaleString()}</td>
                      <td className="px-3 py-3 text-zinc-300">{h.changed_by_name || "—"}</td>
                      <td className="px-3 py-3 text-xs font-mono text-zinc-400">{(h.sales || []).map((s) => `${(s.rate*100).toFixed(1)}%`).join(" · ")}</td>
                      <td className="px-3 py-3 text-xs font-mono text-zinc-400">{(h.leads || []).map((s) => `${(s.rate*100).toFixed(1)}%`).join(" · ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
  );
}

function ScriptsTopic() {
  const askDelete = useDeleteAsk();
  const [scripts, setScripts] = useState([]);
  const [scriptOpen, setScriptOpen] = useState(false);
  const [selectedScript, setSelectedScript] = useState(null);
  const loadScripts = useCallback(() => listScripts().then((s) => {
    setScripts(s);
    setSelectedScript((cur) => (cur && !(s || []).find((x) => x.id === cur.id) ? null : cur));
  }), []);
  useEffect(() => { loadScripts(); }, [loadScripts]);
  const removeScript = async (id) => {
    const reason = askDelete("this script", "Delete this script?");
    if (reason === null) return;
    try { if (deletionRequested(await deleteScript(id, reason))) return; }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete it"); return; }
    toast.success("Script deleted");
    if (selectedScript?.id === id) setSelectedScript(null);
    loadScripts();
  };
  return (
      <section className="space-y-4" data-testid="scripts-section">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-bold">Official sales scripts</h3>
          <Dialog open={scriptOpen} onOpenChange={setScriptOpen}>
            <DialogTrigger data-testid="settings-dialog-trigger-195" asChild>
              <Button className="bg-blue-600 hover:bg-blue-500 rounded-full px-5" data-testid="new-script-btn">
                <Plus className="h-4 w-4 mr-1.5" /> New script
              </Button>
            </DialogTrigger>
            <ScriptDialog onDone={() => { setScriptOpen(false); loadScripts(); }} />
          </Dialog>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 space-y-3">
            {scripts.length === 0 && (
              <div className="card-surface p-10 text-center">
                <FileText className="h-8 w-8 text-zinc-600 mx-auto" strokeWidth={1.5} />
                <h4 className="font-display text-base font-bold mt-4">No scripts yet</h4>
                <p className="text-sm text-zinc-500 mt-1">Upload your first sales script to grade recorded calls against.</p>
              </div>
            )}
            {scripts.map((s) => (
              <button
                key={s.id}
                onClick={() => setSelectedScript(s)}
                data-testid={`script-item-${s.id}`}
                className={`w-full text-left card-surface p-4 transition-colors ${
                  selectedScript?.id === s.id ? "border-blue-500/60 bg-[#101318]" : ""
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-display font-bold truncate">{s.title}</div>
                    <div className="text-xs text-zinc-500 mt-1">
                      {new Date(s.created_at).toLocaleDateString()} · {s.source.toUpperCase()} · {s.content.length.toLocaleString()} chars
                    </div>
                  </div>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); removeScript(s.id); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); removeScript(s.id); } }}
                    className="text-zinc-500 hover:text-rose-400 transition-colors p-1 rounded"
                    data-testid={`delete-script-${s.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </span>
                </div>
                <p className="text-xs text-zinc-400 mt-2.5 line-clamp-2">{s.content.slice(0, 180)}</p>
              </button>
            ))}
          </div>

          <div className="lg:col-span-7">
            <div className="card-surface p-6 sticky top-24">
              {selectedScript ? (
                <>
                  <div className="eyebrow">Preview</div>
                  <h4 className="font-display text-xl font-bold tracking-tight mt-1">{selectedScript.title}</h4>
                  <div className="text-xs text-zinc-500 mt-1">Source: {selectedScript.source.toUpperCase()}</div>
                  <div className="mt-5 max-h-[50vh] overflow-y-auto pr-2 whitespace-pre-wrap text-sm text-zinc-300 leading-relaxed" data-testid="script-content-preview">
                    {selectedScript.content}
                  </div>
                </>
              ) : (
                <div className="text-center py-16 text-zinc-500 text-sm">Select a script to preview its full content.</div>
              )}
            </div>
          </div>
        </div>
      </section>
  );
}



const DAY_LABELS_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function SessionScheduleCard({ canEdit }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [qaLabel, setQaLabel] = useState("");
  const [qaTime, setQaTime] = useState("06:00");
  const [qaDays, setQaDays] = useState([]);
  const [copyFrom, setCopyFrom] = useState("0");
  const [copyDays, setCopyDays] = useState([]);

  const load = useCallback(async () => {
    try {
      const data = await listSessionSchedules();
      setRows((data || []).map((r) => ({ ...r, _rid: _newRid() })));
      setLoaded(true);
    } catch {
      toast.error("Could not load session schedules");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addRow = () => setRows((prev) => [...prev, { _rid: _newRid(), day_of_week: 0, start_time: "06:00", label: "", active: true }]);
  const upd = (rid, patch) => setRows((prev) => (prev || []).map((r) => (r._rid === rid ? { ...r, ...patch } : r)));
  const del = (rid) => setRows((prev) => (prev || []).filter((r) => r._rid !== rid));
  const dupRow = (rid) => setRows((prev) => {
    const src = (prev || []).find((r) => r._rid === rid);
    if (!src) return prev;
    return [...prev, { ...src, id: undefined, _rid: _newRid() }];
  });

  const isDup = (list, day, time, label) =>
    (list || []).some((r) => Number(r.day_of_week) === day && r.start_time === time &&
      (r.label || "").trim().toLowerCase() === label.trim().toLowerCase());

  const toggleDay = (list, setter, day) =>
    setter((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]));

  const quickAdd = () => {
    const label = qaLabel.trim();
    if (!label) { toast.error("Enter a session label"); return; }
    if (qaDays.length === 0) { toast.error("Pick at least one day"); return; }
    const additions = [];
    let skipped = 0;
    for (const d of [...qaDays].sort((a, b) => a - b)) {
      if (isDup(rows, d, qaTime, label)) { skipped += 1; continue; }
      additions.push({ _rid: _newRid(), day_of_week: d, start_time: qaTime, label, active: true });
    }
    if (additions.length > 0) setRows((prev) => [...prev, ...additions]);
    if (skipped > 0) toast.warning(`Added ${additions.length} · skipped ${skipped} duplicate${skipped === 1 ? "" : "s"}`);
    else toast.success(`Added ${additions.length} session${additions.length === 1 ? "" : "s"} — remember to Save`);
  };

  const copyDay = () => {
    const src = Number(copyFrom);
    const targets = copyDays.filter((d) => d !== src);
    if (targets.length === 0) { toast.error("Pick at least one target day"); return; }
    const srcRows = (rows || []).filter((r) => Number(r.day_of_week) === src);
    if (srcRows.length === 0) { toast.error(`No sessions on ${DAY_LABELS_SHORT[src]} to copy`); return; }
    const additions = [];
    let skipped = 0;
    const working = [...(rows || [])];
    for (const d of [...targets].sort((a, b) => a - b)) {
      for (const r of srcRows) {
        if (isDup(working, d, r.start_time, r.label || "")) { skipped += 1; continue; }
        const clone = { _rid: _newRid(), day_of_week: d, start_time: r.start_time, label: r.label, active: r.active !== false };
        additions.push(clone);
        working.push(clone);
      }
    }
    if (additions.length > 0) setRows((prev) => [...prev, ...additions]);
    if (additions.length === 0) { toast.warning("Nothing new to copy — those sessions already exist"); return; }
    if (skipped > 0) toast.warning(`Copied ${additions.length} · skipped ${skipped} duplicate${skipped === 1 ? "" : "s"}`);
    else toast.success(`Copied ${additions.length} session${additions.length === 1 ? "" : "s"} — remember to Save`);
  };

  const save = async () => {
    setBusy(true);
    try {
      const totalRows = (rows || []).length;
      const payload = (rows || []).map((r) => ({
        id: r.id,
        day_of_week: Number(r.day_of_week),
        start_time: r.start_time,
        label: (r.label || "").trim(),
        active: r.active !== false,
      })).filter((r) => r.label && r.start_time);
      const dropped = totalRows - payload.length;
      const res = await saveSessionSchedules(payload);
      if (dropped > 0) {
        toast.warning(`Saved ${res.count} · dropped ${dropped} row${dropped === 1 ? "" : "s"} missing a label or time`);
      } else {
        toast.success(`Saved ${res.count} session${res.count === 1 ? "" : "s"}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save schedules");
    } finally {
      setBusy(false);
    }
  };

  const sortedRows = [...(rows || [])].sort(
    (a, b) => Number(a.day_of_week) - Number(b.day_of_week) || String(a.start_time).localeCompare(String(b.start_time)),
  );
  const dayChipCls = (on) => `rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
    on ? "border-emerald-500/60 bg-emerald-500/15 text-emerald-300" : "border-[#262626] bg-[#0a0a0a] text-zinc-400 hover:border-zinc-500"}`;

  return (
    <div className="card-surface p-6" data-testid="session-schedules-card">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-emerald-400" />
          <h3 className="font-display text-lg font-bold">Session schedules</h3>
          {!canEdit && <Lock className="h-3.5 w-3.5 text-zinc-500" />}
        </div>
        {canEdit && (
          <Button variant="outline" size="sm" onClick={addRow} data-testid="session-add"><Plus className="mr-1 h-3.5 w-3.5" /> Add blank row</Button>
        )}
      </div>
      <p className="mb-3 text-xs text-zinc-500">
        Recurring weekly sessions used by the <span className="text-zinc-300">tablet check-in page</span>. The coach picks the right session when members start walking in.
      </p>

      {canEdit && (
        <div className="mb-4 rounded-md border border-[#1f1f1f] bg-[#0c0c0c] p-3" data-testid="session-quick-add">
          <div className="mb-2 text-[11px] font-medium uppercase tracking-[0.18em] text-zinc-500">Quick add — one session, many days</div>
          <div className="flex flex-wrap items-center gap-2">
            <Input value={qaLabel} onChange={(e) => setQaLabel(e.target.value)} placeholder="e.g. Group HIIT" className="w-full sm:w-44" data-testid="session-qa-label" />
            <Input type="time" value={qaTime} onChange={(e) => setQaTime(e.target.value)} className="w-28" data-testid="session-qa-time" />
            <div className="flex flex-wrap gap-1">
              {DAY_LABELS_SHORT.map((d, i) => (
                <button key={d} type="button" onClick={() => toggleDay(qaDays, setQaDays, i)} className={dayChipCls(qaDays.includes(i))} data-testid={`session-qa-day-${i}`}>{d}</button>
              ))}
            </div>
            <Button size="sm" onClick={quickAdd} data-testid="session-qa-submit"><Plus className="mr-1 h-3.5 w-3.5" /> Add</Button>
          </div>
        </div>
      )}

      {!loaded ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-dashed border-[#262626] px-4 py-6 text-center text-sm text-zinc-500">
          No sessions yet. {canEdit ? "Use Quick add above to create several at once." : "Ask a manager to set them up."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="session-schedules-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.18em] text-zinc-500 border-b border-[#1f1f1f]">
                <th className="px-2 py-2 font-medium">Day</th>
                <th className="px-2 py-2 font-medium">Start</th>
                <th className="px-2 py-2 font-medium">Label</th>
                <th className="px-2 py-2 font-medium text-center">Active</th>
                <th className="px-2 py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((r) => (
                <tr key={r._rid} className="border-b border-[#161616]" data-testid={`session-row-${r._rid}`}>
                  <td className="px-2 py-1.5">
                    <select
                      disabled={!canEdit}
                      value={r.day_of_week}
                      onChange={(e) => upd(r._rid, { day_of_week: Number(e.target.value) })}
                      className="w-full rounded border border-input bg-[#0a0a0a] px-2 py-1.5 text-sm"
                      data-testid={`session-dow-${r._rid}`}
                    >
                      {DAY_LABELS_SHORT.map((d, i) => <option key={d} value={i}>{d}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-1.5">
                    <Input disabled={!canEdit} type="time" value={r.start_time} onChange={(e) => upd(r._rid, { start_time: e.target.value })} className="w-28" data-testid={`session-time-${r._rid}`} />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input disabled={!canEdit} value={r.label} onChange={(e) => upd(r._rid, { label: e.target.value })} placeholder="e.g. Group HIIT" data-testid={`session-label-${r._rid}`} />
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    <input
                      type="checkbox"
                      disabled={!canEdit}
                      checked={r.active !== false}
                      onChange={(e) => upd(r._rid, { active: e.target.checked })}
                      data-testid={`session-active-${r._rid}`}
                    />
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    {canEdit && (
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" title="Duplicate row" onClick={() => dupRow(r._rid)} data-testid={`session-dup-${r._rid}`}>
                          <Copy className="h-3.5 w-3.5 text-zinc-400" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => del(r._rid)} data-testid={`session-del-${r._rid}`}>
                          <Trash2 className="h-3.5 w-3.5 text-rose-400" />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {canEdit && rows.length > 0 && (
        <div className="mt-3 rounded-md border border-[#1f1f1f] bg-[#0c0c0c] p-3" data-testid="session-copy-day">
          <div className="mb-2 text-[11px] font-medium uppercase tracking-[0.18em] text-zinc-500">Replicate a whole day</div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-zinc-500">Copy</span>
            <select value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)} className="rounded border border-input bg-[#0a0a0a] px-2 py-1.5 text-sm" data-testid="session-copy-source">
              {DAY_LABELS_SHORT.map((d, i) => <option key={d} value={i}>{d}</option>)}
            </select>
            <span className="text-xs text-zinc-500">→</span>
            <div className="flex flex-wrap gap-1">
              {DAY_LABELS_SHORT.map((d, i) => (
                <button key={d} type="button" onClick={() => toggleDay(copyDays, setCopyDays, i)} className={dayChipCls(copyDays.includes(i))} data-testid={`session-copy-day-${i}`}>{d}</button>
              ))}
            </div>
            <Button size="sm" variant="outline" onClick={copyDay} data-testid="session-copy-submit"><Copy className="mr-1 h-3.5 w-3.5" /> Copy day</Button>
          </div>
        </div>
      )}

      {canEdit && (
        <div className="mt-3 flex justify-end">
          <Button onClick={save} disabled={busy} data-testid="session-save">
            {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />}
            Save sessions
          </Button>
        </div>
      )}
    </div>
  );
}


function ScriptDialog({ onDone }) {
  const [tab, setTab] = useState("paste");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error("Give the script a title");
    setBusy(true);
    try {
      if (tab === "paste") {
        if (!content.trim()) { setBusy(false); return toast.error("Paste the script content"); }
        await createScript({ title, content });
      } else {
        if (!file) { setBusy(false); return toast.error("Choose a PDF or DOCX file"); }
        await uploadScript(title, file);
      }
      toast.success("Script saved");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-2xl" data-testid="script-dialog">
      <DialogHeader>
        <DialogTitle className="font-display text-xl">New sales script</DialogTitle>
      </DialogHeader>
      <form data-testid="settings-form-377" onSubmit={submit} className="space-y-4 mt-2">
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Title</Label>
          <Input
            data-testid="script-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Discovery Call — SMB v3"
            className="bg-[#0a0a0a] border-[#262626] mt-2"
          />
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-[#0a0a0a] border border-[#262626]">
            <TabsTrigger value="paste" data-testid="tab-paste">Paste text</TabsTrigger>
            <TabsTrigger value="upload" data-testid="tab-upload">Upload PDF/DOCX</TabsTrigger>
          </TabsList>
          <TabsContent value="paste" className="mt-3">
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Opening… Value proposition… Discovery questions… Objection handling… Close…"
              className="bg-[#0a0a0a] border-[#262626] min-h-[240px] font-mono text-xs"
              data-testid="script-content-input"
            />
          </TabsContent>
          <TabsContent value="upload" className="mt-3">
            <label
              htmlFor="script-file"
              className="flex flex-col items-center justify-center border border-dashed border-[#333] rounded-xl px-6 py-10 cursor-pointer hover:border-blue-500/60 hover:bg-[#0d0d0d] transition-colors"
              data-testid="script-dropzone"
            >
              <UploadCloud className="h-6 w-6 text-zinc-500 mb-2" />
              <div className="text-sm text-zinc-300">{file ? file.name : "Choose a PDF or DOCX file"}</div>
              <div className="text-[11px] text-zinc-500 mt-1">We&apos;ll extract the text automatically</div>
              <input
                id="script-file"
                type="file"
                accept=".pdf,.docx,.txt"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="script-file-input"
              />
            </label>
          </TabsContent>
        </Tabs>
        <DialogFooter>
          <Button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-500 rounded-full" data-testid="script-submit-btn">
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</> : "Save script"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}



function RenewalSettings() {
  const [days, setDays] = useState(30);
  const [saved, setSaved] = useState(30);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    getRenewalSettings().then((r) => { setDays(r.window_days); setSaved(r.window_days); }).catch(() => {});
  }, []);
  const save = async () => {
    setBusy(true);
    try {
      const n = Math.max(1, Math.min(180, Number(days) || 30));
      const r = await setRenewalSettings(n);
      setSaved(r.window_days);
      toast.success("Renewal window updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };
  const dirty = Number(days) !== Number(saved);
  return (
    <section className="card-surface p-6" data-testid="renewal-settings-section">
      <div className="flex items-center gap-2 mb-2">
        <BellRing className="h-4 w-4 text-amber-400" />
        <h3 className="font-display text-lg font-bold">Notifications · Renewal window</h3>
      </div>
      <p className="text-xs text-zinc-400 mb-4">Members whose programme end date is within this many days (or already past) show up on the Dashboard <span className="text-zinc-300">Notifications</span> panel until they renew.</p>
      <div className="flex items-end gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Nudge window (days)</Label>
          <Input type="number" min={1} max={180} value={days} onChange={(e) => setDays(e.target.value)} className="bg-[#0a0a0a] border-[#262626] w-32 font-mono mt-1.5" data-testid="renewal-days-input" />
        </div>
        <Button onClick={save} disabled={busy || !dirty} className="bg-blue-600 hover:bg-blue-500 rounded-full" data-testid="save-renewal-days-btn">
          <Save className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save"}
        </Button>
      </div>
    </section>
  );
}


// Per-facility taxation config. Editable by manager+ at the caller's active facility (or any
// facility for master admin). Reads current values from useAuth().activeFacility so the form
// stays in sync with the facility switcher — no separate GET needed.
// Per-gym Accountability options (CR-0024). Gyms with no blood tests (Colombo) switch it off:
// the Blood tests columns and the "Blood tests missing" card disappear for that gym.
function AccountabilitySettings() {
  const { activeFacility, user, refresh } = useAuth();
  const canEdit = atLeast(user?.role, "manager");
  const on = activeFacility?.offers_blood_tests !== false;
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    if (!activeFacility?.id) return;
    setBusy(true);
    try {
      await setFacilityAccountability(activeFacility.id, { offers_blood_tests: !on });
      toast.success(on ? `Blood tests hidden for ${activeFacility.name}` : `Blood tests shown for ${activeFacility.name}`);
      refresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="rounded-2xl border border-[#1f1f1f] bg-[#0f0f0f]/60 p-5" data-testid="accountability-settings-section">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Accountability · {activeFacility?.name || "—"}</div>
          <h3 className="font-display text-lg font-bold mt-1">Blood tests</h3>
          <p className="text-xs text-zinc-500 mt-1 max-w-xl">
            Turn off if this gym doesn&apos;t offer blood tests. The Blood tests columns and the &quot;Blood tests missing&quot; card
            are hidden on Accountability. Nothing already saved is deleted.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 text-xs text-zinc-300 whitespace-nowrap select-none">
          <input type="checkbox" checked={on} onChange={toggle} disabled={!canEdit || busy}
            className="h-4 w-4 rounded border-[#333] bg-[#0a0a0a] accent-emerald-500" data-testid="offers-blood-tests-toggle" />
          <span className={on ? "text-emerald-300" : "text-zinc-500"}>{on ? "Offers blood tests" : "No blood tests"}</span>
        </label>
      </div>
    </section>
  );
}

function TaxationSettings() {
  const { activeFacility, user, refresh } = useAuth();
  const canEdit = atLeast(user?.role, "manager");
  const facId = activeFacility?.id;
  const initialName = activeFacility?.tax_name ?? "GST";
  const initialRatePct = ((activeFacility?.tax_rate ?? 0.05) * 100).toString();
  const initialEnabled = activeFacility?.tax_enabled ?? true;
  const [name, setName] = useState(initialName);
  const [ratePct, setRatePct] = useState(initialRatePct);
  const [enabled, setEnabled] = useState(initialEnabled);
  const [busy, setBusy] = useState(false);
  const dirty =
    name.trim() !== initialName ||
    ratePct.trim() !== initialRatePct.trim() ||
    enabled !== initialEnabled;

  useEffect(() => {
    setName(initialName);
    setRatePct(initialRatePct);
    setEnabled(initialEnabled);
  }, [facId, initialName, initialRatePct, initialEnabled]);

  const save = async () => {
    if (!facId) return toast.error("No active facility — pick one from the top-right switcher first");
    const pct = parseFloat(ratePct);
    if (Number.isNaN(pct) || pct < 0 || pct >= 100) {
      return toast.error("Rate must be a number between 0 and 100 (percent)");
    }
    if (!name.trim()) return toast.error("Tax name cannot be blank");
    setBusy(true);
    try {
      await setFacilityTax(facId, {
        tax_name: name.trim(),
        tax_rate: Math.round(pct * 10000) / 1_000_000, // 5.0 → 0.05, keep 4 decimal places
        tax_enabled: enabled,
      });
      toast.success(`Tax config saved for ${activeFacility?.name}`);
      refresh?.(); // pull the updated facility back into AuthContext so everywhere sees the new rate
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save tax config");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-2xl border border-[#1f1f1f] bg-[#0f0f0f]/60 p-5 space-y-4" data-testid="taxation-settings-section">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="eyebrow flex items-center gap-2">
            <span>Taxation · {activeFacility?.name || "—"}</span>
          </div>
          <h3 className="font-display text-lg font-bold mt-1">Consumption tax on sales</h3>
          <p className="text-xs text-zinc-500 mt-1 max-w-xl">
            Applied to every sale automatically. India uses GST at 5%. Sri Lanka uses VAT. UAE has no consumption tax
            (toggle Enabled off). Changing the rate does <span className="text-zinc-300">not</span> re-cost historical sales —
            only new sales and payments will use the updated value.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 text-xs text-zinc-300 whitespace-nowrap select-none" data-testid="tax-enabled-toggle-wrap">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            disabled={!canEdit}
            className="h-4 w-4 rounded border-[#333] bg-[#0a0a0a] accent-emerald-500"
            data-testid="tax-enabled-toggle"
          />
          <span className={enabled ? "text-emerald-300" : "text-zinc-500"}>{enabled ? "Charging tax" : "Tax disabled"}</span>
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Tax label</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={!canEdit}
            placeholder="e.g. GST, VAT, Sales Tax"
            className="bg-[#0a0a0a] border-[#262626] mt-1.5"
            data-testid="tax-name-input"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Rate (%)</Label>
          <Input
            type="number"
            step="0.01"
            min="0"
            max="99.99"
            value={ratePct}
            onChange={(e) => setRatePct(e.target.value)}
            disabled={!canEdit || !enabled}
            className="bg-[#0a0a0a] border-[#262626] mt-1.5"
            data-testid="tax-rate-input"
          />
          <p className="text-[10px] text-zinc-500 mt-1">Enter 5 for 5%. India GST = 5, Sri Lanka VAT ≈ 15 or 18 depending on package.</p>
        </div>
        <div className="flex items-end">
          <Button
            onClick={save}
            disabled={!canEdit || !dirty || busy}
            className="bg-emerald-600 hover:bg-emerald-500 rounded-full w-full"
            data-testid="tax-save-btn"
          >
            {busy ? "Saving…" : dirty ? "Save tax config" : "No changes"}
          </Button>
        </div>
      </div>
    </section>
  );
}


// Pausable unless set: not the 6 Week Challenge, not LIFTR PRO, longer than 4 weeks (same as the server).
const canPause = (r) => {
  if (r.pausable !== undefined && r.pausable !== null) return r.pausable === true;
  const name = (r.name || "").toLowerCase();
  const sixWeek = ["6 week", "6-week", "six week", "6wc"].some((k) => name.includes(k)) && !name.includes("annual");
  return !sixWeek && !name.includes("liftr pro") && (Number(r.duration_days) || 0) > 28;
};

function ProgrammeCatalog({ canEdit }) {
  const { currency } = useAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const fmtINR = (n) => new Intl.NumberFormat(numberLocale(), { maximumFractionDigits: 0 }).format(n || 0);

  const load = useCallback(() => getProgrammeCatalog().then((r) => { setItems(withRids(r.items || [])); setDirty(false); }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const update = (i, k, v) => {
    setItems((rows) => {
      const next = [...rows];
      const row = { ...next[i], [k]: k === "price" || k === "duration_days" ? (v === "" ? 0 : Number(v)) : v };
      // If the user changes surface and hasn't explicitly toggled accountability, auto-default the flag.
      if (k === "surface" && row.accountability_entry === undefined) {
        row.accountability_entry = v === "front_end" || v === "both";
      }
      next[i] = row;
      return next;
    });
    setDirty(true);
  };
  const remove = (i) => { setItems((rows) => (rows || []).filter((_, idx) => idx !== i)); setDirty(true); };
  const add = () => {
    setItems((rows) => [...rows, { _rid: _newRid(), name: "", surface: "all_sales", price: 0, duration_days: 0, active: true, accountability_entry: false, blood_test: false, blood_test_count: 0, blood_test_unit_price: 0 }]);
    setDirty(true);
  };
  const save = async () => {
    for (const r of items) {
      if (!r.name || !r.name.trim()) return toast.error("Every programme needs a name");
      if (Number(r.price) < 0) return toast.error("Price cannot be negative");
      if (Number(r.duration_days) < 0) return toast.error("Duration cannot be negative");
    }
    setBusy(true);
    try {
      const r = await setProgrammeCatalog(stripRids(items));
      setItems(withRids(r.items || []));
      setDirty(false);
      toast.success("Programme catalog saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <section className="card-surface p-6" data-testid="programme-catalog-section">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-blue-400" />
          <div>
            <h3 className="font-display text-lg font-bold">Programme catalog</h3>
            <div className="text-xs text-zinc-400 mt-0.5">Define the price and duration of each programme. Prices are before tax and auto-populate on New Sale forms; sales reps can still override with a full audit trail. A 6WC finisher price is what members who have finished a 6 Week Challenge pay; an Annual paid in parts is 10% more. Every active programme can be picked on both New Consult and All Sales.</div>
          </div>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={add} className="border-[#262626] bg-transparent hover:bg-[#141414]" data-testid="programme-add-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add programme
            </Button>
            <Button onClick={save} disabled={busy || !dirty} className="bg-blue-600 hover:bg-blue-500 rounded-full" data-testid="programme-save-btn">
              <Save className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save catalog"}
            </Button>
          </div>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 border-b border-[#1f1f1f]">
              <th className="px-3 py-2 font-medium">Name</th>
              <th className="px-3 py-2 font-medium text-right">Price ({currency})</th>
              <th className="px-3 py-2 font-medium text-right" title="What members who have finished a 6 Week Challenge pay. Leave empty for the same price.">6WC finisher price</th>
              <th className="px-3 py-2 font-medium text-right" title="What it costs when paid in parts, if that's different (e.g. a 6WC: 80k in full, 85k in parts). Annual in parts is already +10%.">Paid in parts</th>
              <th className="px-3 py-2 font-medium text-right">Duration (days)</th>
              <th className="px-3 py-2 font-medium">Enters Accountability</th>
              <th className="px-3 py-2 font-medium" title="Members can pause it in the app: 1 day per programme week, at least 3 days per pause">Pausable</th>
              <th className="px-3 py-2 font-medium">Blood test</th>
              <th className="px-3 py-2 font-medium">Active</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((r, i) => (
              <tr key={r._rid || i} className="border-b border-[#1a1a1a] last:border-0" data-testid={`programme-row-${i}`}>
                <td className="px-3 py-2">
                  <Input value={r.name || ""} onChange={(e) => update(i, "name", e.target.value)} readOnly={!canEdit} className="bg-[#0a0a0a] border-[#262626]" data-testid={`programme-name-${i}`} />
                </td>
                <td className="px-3 py-2 text-right">
                  <Input type="number" value={r.price ?? 0} onChange={(e) => update(i, "price", e.target.value)} readOnly={!canEdit} className="bg-[#0a0a0a] border-[#262626] font-mono text-right" data-testid={`programme-price-${i}`} />
                  <div className="text-[10px] text-zinc-500 mt-0.5">{currency}{fmtINR(r.price)}</div>
                </td>
                <td className="px-3 py-2 text-right">
                  <Input type="number" min="0" value={r.finisher_price ?? ""} placeholder="Same"
                    onChange={(e) => update(i, "finisher_price", e.target.value === "" ? null : Number(e.target.value))}
                    readOnly={!canEdit} className="bg-[#0a0a0a] border-[#262626] font-mono text-right" data-testid={`programme-finisher-price-${i}`} />
                  {r.finisher_price ? <div className="text-[10px] text-zinc-500 mt-0.5">{currency}{fmtINR(r.finisher_price)}</div> : null}
                </td>
                <td className="px-3 py-2 text-right">
                  <Input type="number" min="0" value={r.parts_price ?? ""} placeholder="Same"
                    onChange={(e) => update(i, "parts_price", e.target.value === "" ? null : Number(e.target.value))}
                    readOnly={!canEdit} className="bg-[#0a0a0a] border-[#262626] font-mono text-right" data-testid={`programme-parts-price-${i}`} />
                  {r.parts_price ? <div className="text-[10px] text-zinc-500 mt-0.5">{currency}{fmtINR(r.parts_price)}</div> : null}
                </td>
                <td className="px-3 py-2 text-right">
                  <Input type="number" value={r.duration_days ?? 0} onChange={(e) => update(i, "duration_days", e.target.value)} readOnly={!canEdit} className="bg-[#0a0a0a] border-[#262626] font-mono text-right" data-testid={`programme-days-${i}`} />
                </td>
                <td className="px-3 py-2">
                  <label className="inline-flex items-center gap-1.5 text-xs text-zinc-300">
                    <input
                      type="checkbox"
                      checked={r.accountability_entry !== false && (r.accountability_entry === true || r.surface === "front_end" || r.surface === "both")}
                      onChange={(e) => update(i, "accountability_entry", e.target.checked)}
                      disabled={!canEdit}
                      data-testid={`programme-accountability-${i}`}
                    />
                    {(r.accountability_entry === true || (r.accountability_entry === undefined && (r.surface === "front_end" || r.surface === "both"))) ? "Yes" : "No"}
                  </label>
                </td>
                <td className="px-3 py-2">
                  <label className="inline-flex items-center gap-1.5 text-xs text-zinc-300">
                    <input
                      type="checkbox"
                      checked={canPause(r)}
                      onChange={(e) => update(i, "pausable", e.target.checked)}
                      disabled={!canEdit}
                      data-testid={`programme-pausable-${i}`}
                    />
                    {canPause(r) ? `${Math.floor((Number(r.duration_days) || 0) / 7)} days` : "No"}
                  </label>
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <label className="inline-flex items-center gap-1.5 text-xs text-zinc-300">
                      <input
                        type="checkbox"
                        checked={r.blood_test === true}
                        onChange={(e) => update(i, "blood_test", e.target.checked)}
                        disabled={!canEdit}
                        data-testid={`programme-blood-test-${i}`}
                      />
                      {r.blood_test ? "On" : "Off"}
                    </label>
                    {r.blood_test && (
                      <div className="flex items-center gap-1">
                        <Input
                          type="number" min="0" value={r.blood_test_count ?? 0}
                          onChange={(e) => update(i, "blood_test_count", parseInt(e.target.value) || 0)}
                          readOnly={!canEdit} className="bg-[#0a0a0a] border-[#262626] font-mono text-right w-16 h-8"
                          data-testid={`programme-bt-count-${i}`}
                          title="Number of tests included"
                        />
                        <span className="text-[10px] text-zinc-500">×</span>
                        <Input
                          type="number" min="0" step="0.01" value={r.blood_test_unit_price ?? 0}
                          onChange={(e) => update(i, "blood_test_unit_price", parseFloat(e.target.value) || 0)}
                          readOnly={!canEdit} className="bg-[#0a0a0a] border-[#262626] font-mono text-right w-24 h-8"
                          data-testid={`programme-bt-price-${i}`}
                          title="Wholesale cost per test to the gym"
                        />
                        <span className="text-[10px] text-emerald-400 font-mono ml-1">
                          −{fmtINR((r.blood_test_count || 0) * (r.blood_test_unit_price || 0))}
                        </span>
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <label className="inline-flex items-center gap-1.5 text-xs text-zinc-300">
                    <input type="checkbox" checked={r.active !== false} onChange={(e) => { update(i, "active", e.target.checked); }} disabled={!canEdit} data-testid={`programme-active-${i}`} />
                    {r.active !== false ? "On" : "Off"}
                  </label>
                </td>
                <td className="px-3 py-2 text-right">
                  {canEdit && (
                    <button onClick={() => remove(i)} className="text-zinc-500 hover:text-rose-400 p-1" data-testid={`programme-remove-${i}`} aria-label="Remove"><Trash2 className="h-4 w-4" /></button>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-xs text-zinc-500">No programmes defined yet — click &quot;Add programme&quot; to create one.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function OverrideLogPanel({ canView }) {
  const { currency } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => listOverrideLog({ limit: 50 }).then(setRows).catch(() => setRows([])), []);
  useEffect(() => { if (canView && open) load(); }, [canView, open]);
  if (!canView) return null;
  const fmtINR = (n) => new Intl.NumberFormat(numberLocale(), { maximumFractionDigits: 0 }).format(n || 0);
  return (
    <section className="card-surface p-6" data-testid="override-log-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-amber-400" />
          <div>
            <h3 className="font-display text-lg font-bold">Price override audit</h3>
            <div className="text-xs text-zinc-400 mt-0.5">Every sale/lead entered with an amount that differs from the programme catalog price is recorded here.</div>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => setOpen((v) => !v)} className="border-[#262626] bg-transparent hover:bg-[#141414]" data-testid="override-log-toggle">
          {open ? "Hide log" : "Show log"}
        </Button>
      </div>
      {open && (
        <div className="mt-4 overflow-x-auto">
          {rows.length === 0 ? (
            <div className="text-xs text-zinc-500 text-center py-6">No overrides recorded yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 border-b border-[#1f1f1f]">
                  <th className="px-3 py-2 font-medium">When</th>
                  <th className="px-3 py-2 font-medium">Who</th>
                  <th className="px-3 py-2 font-medium">Type</th>
                  <th className="px-3 py-2 font-medium">Programme</th>
                  <th className="px-3 py-2 font-medium text-right">Catalog</th>
                  <th className="px-3 py-2 font-medium text-right">Entered</th>
                  <th className="px-3 py-2 font-medium">Note</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id || i} className="border-b border-[#1a1a1a] last:border-0 hover:bg-[#161616]" data-testid={`override-row-${i}`}>
                    <td className="px-3 py-2 text-zinc-400 text-xs whitespace-nowrap">{r.at ? new Date(r.at).toLocaleString() : "—"}</td>
                    <td className="px-3 py-2 text-zinc-300 text-xs">{r.user_name || "—"}</td>
                    <td className="px-3 py-2 text-zinc-400 text-xs uppercase">{r.entity_type || "—"}</td>
                    <td className="px-3 py-2 text-zinc-400 text-xs">{r.programme || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono text-zinc-400 text-xs">{currency}{fmtINR(r.catalog_value)}</td>
                    <td className="px-3 py-2 text-right font-mono text-amber-300 text-xs">{currency}{fmtINR(r.entered_value)}</td>
                    <td className="px-3 py-2 text-zinc-500 text-xs truncate max-w-[16rem]" title={r.note}>{r.note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </section>
  );
}

function SaleTypesCatalog({ canEdit }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const load = useCallback(() => getSaleTypes().then((r) => { setItems(withRids(r.items || [])); setDirty(false); }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const update = (i, k, v) => {
    setItems((rows) => {
      const next = [...rows];
      next[i] = { ...next[i], [k]: v };
      return next;
    });
    setDirty(true);
  };
  const remove = (i) => { setItems((rows) => (rows || []).filter((_, idx) => idx !== i)); setDirty(true); };
  const add = () => { setItems((rows) => [...rows, { _rid: _newRid(), name: "", active: true }]); setDirty(true); };
  const save = async () => {
    for (const r of items) {
      if (!r.name || !r.name.trim()) return toast.error("Every sale type needs a name");
    }
    setBusy(true);
    try {
      const r = await setSaleTypes(stripRids(items));
      setItems(withRids(r.items || []));
      setDirty(false);
      toast.success("Sale types saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };
  return (
    <section className="card-surface p-6" data-testid="sale-types-section">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Tag className="h-4 w-4 text-orange-400" />
          <div>
            <h3 className="font-display text-lg font-bold">Sale types (All Sales · Type dropdown)</h3>
            <div className="text-xs text-zinc-400 mt-0.5">Manage the options in the &quot;Type&quot; dropdown on the New Sale form (PIF / PT / SPT / Day Pass / Pro / Tshirt / Event / Other …). Toggle any off without deleting.</div>
          </div>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={add} className="border-[#262626] bg-transparent hover:bg-[#141414]" data-testid="sale-type-add-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add type
            </Button>
            <Button onClick={save} disabled={busy || !dirty} className="bg-orange-600 hover:bg-orange-500 rounded-full" data-testid="sale-type-save-btn">
              <Save className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save types"}
            </Button>
          </div>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 border-b border-[#1f1f1f]">
              <th className="px-3 py-2 font-medium">Name</th>
              <th className="px-3 py-2 font-medium">Active</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((r, i) => (
              <tr key={r._rid || i} className="border-b border-[#1a1a1a] last:border-0" data-testid={`sale-type-row-${i}`}>
                <td className="px-3 py-2">
                  <Input value={r.name || ""} onChange={(e) => update(i, "name", e.target.value)} readOnly={!canEdit} className="bg-[#0a0a0a] border-[#262626]" data-testid={`sale-type-name-${i}`} />
                </td>
                <td className="px-3 py-2">
                  <label className="inline-flex items-center gap-1.5 text-xs text-zinc-300">
                    <input type="checkbox" checked={r.active !== false} onChange={(e) => update(i, "active", e.target.checked)} disabled={!canEdit} data-testid={`sale-type-active-${i}`} />
                    {r.active !== false ? "On" : "Off"}
                  </label>
                </td>
                <td className="px-3 py-2 text-right">
                  {canEdit && (
                    <button onClick={() => remove(i)} className="text-zinc-500 hover:text-rose-400 p-1" data-testid={`sale-type-remove-${i}`} aria-label="Remove"><Trash2 className="h-4 w-4" /></button>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={3} className="px-3 py-8 text-center text-xs text-zinc-500">No sale types yet — click &quot;Add type&quot; to create one.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// Seller details printed on bills for the active gym (Settings → Billing details).
const BILLING_FIELDS = [
  { key: "legal_name", label: "Legal / registered name", placeholder: "FullPower Fitness LLP", required: true },
  { key: "tax_id", label: "Tax number (GSTIN / VAT)", placeholder: "29ABCDE1234F1Z5" },
  { key: "invoice_prefix", label: "Invoice prefix", placeholder: "JPN → JPN/26-27/0001", required: true },
  { key: "sac_code", label: "SAC code", placeholder: "999723 (gyms) — default" },
  { key: "email", label: "Billing email (replies go here)", placeholder: "jpnagar@liftr.fit" },
  { key: "phone", label: "Phone on bill", placeholder: "+91 98765 43210" },
];
function BillingDetailsCard({ canEdit }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { getBillingDetails().then((r) => { setData(r); setForm(r.billing || {}); }).catch(() => {}); }, []);
  if (!data) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const r = await setBillingDetails(form);
      setForm(r.billing);  // token is write-only, so the field clears after saving
      setData((d) => ({ ...d, ghl_connected: r.ghl_connected }));
      toast.success("Billing details saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };
  return (
    <section className="card-surface p-6" data-testid="billing-details-section">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Tag className="h-4 w-4 text-amber-400" />
          <div>
            <h3 className="font-display text-lg font-bold">Billing details · {data.facility_name}</h3>
            <div className="text-xs text-zinc-400 mt-0.5">
              Printed on every bill from this gym. Bills are numbered per gym per financial year (April–March).
              {data.ghl_connected
                ? ` Bills are emailed to the member from ${data.email_from} through this gym's GoHighLevel sub-account.`
                : " Connect this gym's GoHighLevel sub-account below to email bills automatically. Until then, bills can still be generated and downloaded."}
            </div>
          </div>
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            {data.ghl_connected && (
              <Button type="button" variant="outline" disabled={busy} className="rounded-full border-[#262626]" data-testid="billing-test-email"
                onClick={async () => {
                  setBusy(true);
                  try {
                    const r = await sendTestBill();
                    r.sent ? toast.success(`Test bill sent to ${r.to}`) : toast.error(r.reason, { duration: 12000 });
                  } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send the test bill"); }
                  finally { setBusy(false); }
                }}>
                Send me a test bill
              </Button>
            )}
            <Button onClick={save} disabled={busy} className="bg-amber-600 hover:bg-amber-500 rounded-full" data-testid="billing-save-btn">
              <Save className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save details"}
            </Button>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {BILLING_FIELDS.map((f) => (
          <div key={f.key}>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{f.label}{f.required && <span className="text-rose-400 ml-1">*</span>}</Label>
            <Input value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} readOnly={!canEdit} placeholder={f.placeholder} className="mt-1.5 bg-[#0a0a0a] border-[#262626]" data-testid={`billing-${f.key}`} />
          </div>
        ))}
        <div className="md:col-span-2">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Address<span className="text-rose-400 ml-1">*</span></Label>
          <Textarea value={form.address || ""} onChange={(e) => set("address", e.target.value)} readOnly={!canEdit} rows={3} placeholder={"Street, area\nCity, State PIN"} className="mt-1.5 bg-[#0a0a0a] border-[#262626]" data-testid="billing-address" />
        </div>
        <div className="md:col-span-2 rounded-xl border border-[#1f1f1f] p-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="billing-ghl">
          <div className="md:col-span-2 flex items-center justify-between gap-2 flex-wrap">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">GoHighLevel · email bills from {(data.email_from || "").replace(/^.*</, "").replace(">", "")}</div>
            <span className={`text-[10px] font-medium rounded-full px-2 py-0.5 border ${data.ghl_connected ? "text-emerald-300 border-emerald-500/30 bg-emerald-500/10" : "text-zinc-400 border-[#262626]"}`}>
              {data.ghl_connected ? "Connected" : "Not connected"}
            </span>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Sub-account (location) ID</Label>
            <Input value={form.ghl_location_id || ""} onChange={(e) => set("ghl_location_id", e.target.value)} readOnly={!canEdit} placeholder="Settings → Business Profile in GoHighLevel" className="mt-1.5 bg-[#0a0a0a] border-[#262626] font-mono" data-testid="billing-ghl_location_id" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Private integration token</Label>
            <Input type="password" autoComplete="off" value={form.ghl_token || ""} onChange={(e) => set("ghl_token", e.target.value)} readOnly={!canEdit}
              placeholder={data.ghl_connected ? "Saved — leave blank to keep, type - to remove" : "Paste the token (stored securely, never shown again)"}
              className="mt-1.5 bg-[#0a0a0a] border-[#262626] font-mono" data-testid="billing-ghl_token" />
          </div>
        </div>
        <div className="md:col-span-2">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Note at the bottom of the bill (optional)</Label>
          <Textarea value={form.footer_note || ""} onChange={(e) => set("footer_note", e.target.value)} readOnly={!canEdit} rows={2} placeholder="e.g. Memberships are non-transferable and non-refundable." className="mt-1.5 bg-[#0a0a0a] border-[#262626]" data-testid="billing-footer" />
        </div>
      </div>
    </section>
  );
}

// Option lists for non-membership sales (All Sales → Category → "What was sold").
function RevenueItemsCatalog({ canEdit }) {
  const [categories, setCategories] = useState({});
  const [items, setItems] = useState({});
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const load = useCallback(() => getRevenueItems().then((r) => {
    setCategories(r.categories || {}); setItems(r.items || {}); setDirty(false);
  }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const addItem = (cat) => {
    const name = (drafts[cat] || "").trim();
    if (!name) return;
    if ((items[cat] || []).some((x) => x.toLowerCase() === name.toLowerCase())) return toast.error(`${name} is already in the list`);
    setItems((s) => ({ ...s, [cat]: [...(s[cat] || []), name] }));
    setDrafts((d) => ({ ...d, [cat]: "" }));
    setDirty(true);
  };
  const removeItem = (cat, name) => { setItems((s) => ({ ...s, [cat]: (s[cat] || []).filter((x) => x !== name) })); setDirty(true); };
  const save = async () => {
    setBusy(true);
    try {
      const r = await setRevenueItems(items);
      setItems(r.items || {});
      setDirty(false);
      toast.success("Options saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };
  return (
    <section className="card-surface p-6" data-testid="revenue-items-section">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Tag className="h-4 w-4 text-emerald-400" />
          <div>
            <h3 className="font-display text-lg font-bold">Other revenue (All Sales · Category)</h3>
            <div className="text-xs text-zinc-400 mt-0.5">Options for sales that aren&apos;t memberships: special programmes, additional services, merch and community events.</div>
          </div>
        </div>
        {canEdit && (
          <Button onClick={save} disabled={busy || !dirty} className="bg-emerald-600 hover:bg-emerald-500 rounded-full" data-testid="revenue-items-save-btn">
            <Save className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save options"}
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(categories).map(([cat, label]) => (
          <div key={cat} className="rounded-xl border border-[#1f1f1f] p-4" data-testid={`revenue-items-${cat}`}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">{label}</div>
            <div className="flex flex-wrap gap-2 mb-3">
              {(items[cat] || []).map((name) => (
                <span key={name} className="inline-flex items-center gap-1.5 rounded-full border border-[#262626] bg-[#0d0d0d] px-3 py-1 text-xs text-zinc-200">
                  {name}
                  {canEdit && (
                    <button onClick={() => removeItem(cat, name)} className="text-zinc-500 hover:text-rose-400" aria-label={`Remove ${name}`}><Trash2 className="h-3 w-3" /></button>
                  )}
                </span>
              ))}
              {(items[cat] || []).length === 0 && <span className="text-xs text-zinc-500">No options yet.</span>}
            </div>
            {canEdit && (
              <div className="flex gap-2">
                <Input value={drafts[cat] || ""} onChange={(e) => setDrafts((d) => ({ ...d, [cat]: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addItem(cat); } }}
                  placeholder="Add option…" className="bg-[#0a0a0a] border-[#262626] h-8 text-xs" />
                <Button variant="outline" size="sm" onClick={() => addItem(cat)} className="border-[#262626] bg-transparent hover:bg-[#141414] h-8">
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function ObjectionCatalog({ canEdit }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const load = useCallback(() => getObjections().then((r) => { setItems(withRids(r.items || [])); setDirty(false); }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const update = (i, k, v) => {
    setItems((rows) => {
      const next = [...rows];
      next[i] = { ...next[i], [k]: v };
      return next;
    });
    setDirty(true);
  };
  const remove = (i) => { setItems((rows) => (rows || []).filter((_, idx) => idx !== i)); setDirty(true); };
  const add = () => { setItems((rows) => [...rows, { _rid: _newRid(), name: "", active: true }]); setDirty(true); };
  const save = async () => {
    for (const r of items) {
      if (!r.name || !r.name.trim()) return toast.error("Every objection needs a name");
    }
    setBusy(true);
    try {
      const r = await setObjections(stripRids(items));
      setItems(withRids(r.items || []));
      setDirty(false);
      toast.success("Objection catalog saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };
  return (
    <section className="card-surface p-6" data-testid="objection-catalog-section">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Tag className="h-4 w-4 text-rose-400" />
          <div>
            <h3 className="font-display text-lg font-bold">Objection catalog (Not-Sold reasons)</h3>
            <div className="text-xs text-zinc-400 mt-0.5">Populates the &quot;Objection&quot; dropdown on the New Sale / Lead form. &quot;Other&quot; is always kept so reps can add free-text if none fit.</div>
          </div>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={add} className="border-[#262626] bg-transparent hover:bg-[#141414]" data-testid="objection-add-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add reason
            </Button>
            <Button onClick={save} disabled={busy || !dirty} className="bg-rose-600 hover:bg-rose-500 rounded-full" data-testid="objection-save-btn">
              <Save className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save reasons"}
            </Button>
          </div>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 border-b border-[#1f1f1f]">
              <th className="px-3 py-2 font-medium">Reason</th>
              <th className="px-3 py-2 font-medium">Active</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((r, i) => (
              <tr key={r._rid || i} className="border-b border-[#1a1a1a] last:border-0" data-testid={`objection-row-${i}`}>
                <td className="px-3 py-2">
                  <Input value={r.name || ""} onChange={(e) => update(i, "name", e.target.value)} readOnly={!canEdit || r.name === "Other"} className="bg-[#0a0a0a] border-[#262626]" data-testid={`objection-name-${i}`} />
                </td>
                <td className="px-3 py-2">
                  <label className="inline-flex items-center gap-1.5 text-xs text-zinc-300">
                    <input type="checkbox" checked={r.active !== false} onChange={(e) => update(i, "active", e.target.checked)} disabled={!canEdit} data-testid={`objection-active-${i}`} />
                    {r.active !== false ? "On" : "Off"}
                  </label>
                </td>
                <td className="px-3 py-2 text-right">
                  {canEdit && r.name !== "Other" && (
                    <button onClick={() => remove(i)} className="text-zinc-500 hover:text-rose-400 p-1" data-testid={`objection-remove-${i}`} aria-label="Remove"><Trash2 className="h-4 w-4" /></button>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={3} className="px-3 py-8 text-center text-xs text-zinc-500">No objections yet — click &quot;Add reason&quot; to create one.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}


function PaymentFeesCatalog({ canEdit }) {
  const { currency } = useAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const load = useCallback(() => getPaymentFees().then((r) => { setItems(withRids(r.items || [])); setDirty(false); }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const update = (i, k, v) => { setItems((rows) => { const n = [...rows]; n[i] = { ...n[i], [k]: v }; return n; }); setDirty(true); };
  const remove = (i) => { setItems((rows) => (rows || []).filter((_, idx) => idx !== i)); setDirty(true); };
  const add = () => { setItems((rows) => [...rows, { _rid: _newRid(), mode: "", percent: 0, flat: 0, active: true }]); setDirty(true); };
  const save = async () => {
    for (const r of items) {
      if (!r.mode || !r.mode.trim()) return toast.error("Every payment mode needs a name");
      if (parseFloat(r.percent) < 0 || parseFloat(r.flat) < 0) return toast.error("Fees must be non-negative");
    }
    setBusy(true);
    try {
      const r = await setPaymentFees(items.map((it) => ({ mode: it.mode.trim(), percent: parseFloat(it.percent) || 0, flat: parseFloat(it.flat) || 0, active: it.active !== false })));
      resetPaymentModesCache();
      setItems(withRids(r.items || [])); setDirty(false);
      toast.success("Payment gateway fees saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <section className="card-surface p-6" data-testid="payment-fees-section">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <CreditCard className="h-4 w-4 text-amber-400" />
          <div>
            <h3 className="font-display text-lg font-bold">Payment gateway fees</h3>
            <div className="text-xs text-zinc-400 mt-0.5">Percentage + flat fee per payment mode. Auto-deducted from Net Received when a sale uses this mode. Refunds do NOT restore the fee (sunk cost).</div>
          </div>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={add} className="border-[#262626] bg-transparent hover:bg-[#141414]" data-testid="payment-fee-add-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add mode
            </Button>
            <Button onClick={save} disabled={busy || !dirty} className="bg-amber-600 hover:bg-amber-500 rounded-full" data-testid="payment-fee-save-btn">
              <Save className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save fees"}
            </Button>
          </div>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 border-b border-[#1f1f1f]">
              <th className="px-3 py-2 font-medium">Payment mode</th>
              <th className="px-3 py-2 font-medium text-right">Percent (%)</th>
              <th className="px-3 py-2 font-medium text-right">Flat ({currency})</th>
              <th className="px-3 py-2 font-medium">Active</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((r, i) => (
              <tr key={r._rid || i} className="border-b border-[#1a1a1a] last:border-0" data-testid={`payment-fee-row-${i}`}>
                <td className="px-3 py-2"><Input value={r.mode || ""} onChange={(e) => update(i, "mode", e.target.value)} readOnly={!canEdit} className="bg-[#0a0a0a] border-[#262626]" data-testid={`payment-fee-mode-${i}`} /></td>
                <td className="px-3 py-2"><Input type="number" step="0.01" value={r.percent ?? 0} onChange={(e) => update(i, "percent", e.target.value)} readOnly={!canEdit} className="bg-[#0a0a0a] border-[#262626] text-right" data-testid={`payment-fee-percent-${i}`} /></td>
                <td className="px-3 py-2"><Input type="number" step="0.01" value={r.flat ?? 0} onChange={(e) => update(i, "flat", e.target.value)} readOnly={!canEdit} className="bg-[#0a0a0a] border-[#262626] text-right" data-testid={`payment-fee-flat-${i}`} /></td>
                <td className="px-3 py-2"><label className="inline-flex items-center gap-1.5 text-xs text-zinc-300"><input type="checkbox" checked={r.active !== false} onChange={(e) => update(i, "active", e.target.checked)} disabled={!canEdit} data-testid={`payment-fee-active-${i}`} />{r.active !== false ? "On" : "Off"}</label></td>
                <td className="px-3 py-2 text-right">{canEdit && (<button onClick={() => remove(i)} className="text-zinc-500 hover:text-rose-400 p-1" data-testid={`payment-fee-remove-${i}`} aria-label="Remove"><Trash2 className="h-4 w-4" /></button>)}</td>
              </tr>
            ))}
            {items.length === 0 && (<tr><td colSpan={5} className="px-3 py-8 text-center text-xs text-zinc-500">No payment modes yet — click &quot;Add mode&quot;.</td></tr>)}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function AuditExercisesCatalog({ canEdit }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const load = useCallback(() => getAuditExercises().then((r) => { setItems(withRids(r.items || [])); setDirty(false); }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const update = (i, k, v) => { setItems((rows) => { const n = [...rows]; n[i] = { ...n[i], [k]: v }; return n; }); setDirty(true); };
  const remove = (i) => { setItems((rows) => (rows || []).filter((_, idx) => idx !== i)); setDirty(true); };
  const add = () => { setItems((rows) => [...rows, { _rid: _newRid(), name: "", unit: "", active: true }]); setDirty(true); };
  const save = async () => {
    for (const r of items) {
      if (!r.name || !r.name.trim()) return toast.error("Every exercise needs a name");
    }
    setBusy(true);
    try {
      const r = await setAuditExercises(items.map((it) => ({ name: it.name.trim(), unit: (it.unit || "").trim(), active: it.active !== false })));
      setItems(withRids(r.items || [])); setDirty(false);
      toast.success("Audit exercises saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <section className="card-surface p-6" data-testid="audit-exercises-section">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Dumbbell className="h-4 w-4 text-violet-400" />
          <div>
            <h3 className="font-display text-lg font-bold">Audit exercises</h3>
            <div className="text-xs text-zinc-400 mt-0.5">Physical benchmarks captured in every Member Audit. Add or hide entries here — the audit form on the member detail page renders them dynamically.</div>
          </div>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={add} className="border-[#262626] bg-transparent hover:bg-[#141414]" data-testid="audit-exercise-add-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add exercise
            </Button>
            <Button onClick={save} disabled={busy || !dirty} className="bg-violet-600 hover:bg-violet-500 rounded-full" data-testid="audit-exercise-save-btn">
              <Save className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save exercises"}
            </Button>
          </div>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 border-b border-[#1f1f1f]">
              <th className="px-3 py-2 font-medium">Exercise / metric</th>
              <th className="px-3 py-2 font-medium">Unit</th>
              <th className="px-3 py-2 font-medium">Active</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((r, i) => (
              <tr key={r._rid || i} className="border-b border-[#1a1a1a] last:border-0" data-testid={`audit-exercise-row-${i}`}>
                <td className="px-3 py-2"><Input value={r.name || ""} onChange={(e) => update(i, "name", e.target.value)} readOnly={!canEdit} className="bg-[#0a0a0a] border-[#262626]" data-testid={`audit-exercise-name-${i}`} /></td>
                <td className="px-3 py-2"><Input value={r.unit || ""} onChange={(e) => update(i, "unit", e.target.value)} readOnly={!canEdit} placeholder="kg / cm / sec…" className="bg-[#0a0a0a] border-[#262626]" data-testid={`audit-exercise-unit-${i}`} /></td>
                <td className="px-3 py-2"><label className="inline-flex items-center gap-1.5 text-xs text-zinc-300"><input type="checkbox" checked={r.active !== false} onChange={(e) => update(i, "active", e.target.checked)} disabled={!canEdit} data-testid={`audit-exercise-active-${i}`} />{r.active !== false ? "On" : "Off"}</label></td>
                <td className="px-3 py-2 text-right">{canEdit && (<button onClick={() => remove(i)} className="text-zinc-500 hover:text-rose-400 p-1" data-testid={`audit-exercise-remove-${i}`} aria-label="Remove"><Trash2 className="h-4 w-4" /></button>)}</td>
              </tr>
            ))}
            {items.length === 0 && (<tr><td colSpan={4} className="px-3 py-8 text-center text-xs text-zinc-500">No exercises yet — click &quot;Add exercise&quot;.</td></tr>)}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function FacilitiesPanel({ canManage }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [viewUsersFor, setViewUsersFor] = useState(null);
  const load = useCallback(() => listFacilities().then(setRows).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  if (!canManage) return null;
  return (
    <section className="card-surface p-6" data-testid="facilities-panel">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-blue-400" />
          <div>
            <h3 className="font-display text-lg font-bold">Facilities</h3>
            <div className="text-xs text-zinc-400 mt-0.5">Every gym is a facility with its own country + currency. Users and data belong to a facility. Master Admin only.</div>
          </div>
        </div>
        <Button size="sm" onClick={() => setShowAdd(true)} className="bg-blue-600 hover:bg-blue-500 rounded-full" data-testid="facility-add-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> Add facility
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 border-b border-[#1f1f1f]">
              <th className="px-3 py-2 font-medium">Facility</th>
              <th className="px-3 py-2 font-medium">Country</th>
              <th className="px-3 py-2 font-medium">Currency</th>
              <th className="px-3 py-2 font-medium">Timezone</th>
              <th className="px-3 py-2 font-medium">Active</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((f) => (
              <tr key={f.id} className="border-b border-[#1a1a1a] last:border-0" data-testid={`facility-row-${f.id}`}>
                <td className="px-3 py-2.5 font-medium">{f.name}</td>
                <td className="px-3 py-2.5 text-zinc-300">{f.country}</td>
                <td className="px-3 py-2.5"><span className="inline-flex items-center gap-1.5 rounded bg-blue-500/10 border border-blue-500/20 text-blue-300 px-1.5 py-0.5 font-mono text-[11px]">{f.currency_symbol} {f.currency_code}</span></td>
                <td className="px-3 py-2.5 text-zinc-400 text-xs">{f.timezone}</td>
                <td className="px-3 py-2.5">{f.active ? <span className="text-emerald-300 text-xs">Active</span> : <span className="text-rose-300 text-xs">Deactivated</span>}</td>
                <td className="px-3 py-2.5 text-right space-x-2 whitespace-nowrap">
                  <button onClick={() => setViewUsersFor(f)} className="text-xs text-blue-300 hover:text-blue-200 underline" data-testid={`facility-users-${f.id}`}>Users</button>
                  <button onClick={() => setEditRow(f)} className="text-xs text-zinc-400 hover:text-zinc-200 underline" data-testid={`facility-edit-${f.id}`}>Edit</button>
                  {f.active ? (
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Deactivate ${f.name}? Data will be preserved but no one will see it.`)) return;
                        setBusy(true);
                        try { await deactivateFacility(f.id); toast.success("Deactivated"); load(); }
                        catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                        finally { setBusy(false); }
                      }}
                      className="text-xs text-amber-300 hover:text-amber-200 underline" data-testid={`facility-deactivate-${f.id}`}
                    >Deactivate</button>
                  ) : (
                    <button
                      onClick={async () => {
                        if (!window.confirm(`⚠ Permanently delete ${f.name}?\n\nThis removes catalogs, slabs and user memberships for this facility. Operational data (leads/sales/customers) stays archived. This cannot be undone.`)) return;
                        setBusy(true);
                        try { await deleteFacilityPermanent(f.id); toast.success(`${f.name} deleted`); load(); }
                        catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                        finally { setBusy(false); }
                      }}
                      className="text-xs text-rose-300 hover:text-rose-200 underline" data-testid={`facility-delete-permanent-${f.id}`}
                    >Delete permanently</button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (<tr><td colSpan={6} className="px-3 py-8 text-center text-xs text-zinc-500">No facilities yet.</td></tr>)}
          </tbody>
        </table>
      </div>

      <FacilityEditDialog
        open={showAdd || !!editRow}
        row={editRow}
        onClose={() => { setShowAdd(false); setEditRow(null); }}
        onSaved={() => { setShowAdd(false); setEditRow(null); load(); }}
        busy={busy}
        setBusy={setBusy}
      />

      <FacilityUsersDialog
        facility={viewUsersFor}
        onClose={() => setViewUsersFor(null)}
      />
    </section>
  );
}

function FacilityEditDialog({ open, row, onClose, onSaved, busy, setBusy }) {
  const [f, setF] = useState({ name: "", country: "India", currency_code: "INR", currency_symbol: "₹", timezone: "Asia/Kolkata" });
  useEffect(() => {
    if (row) setF({ name: row.name || "", country: row.country || "", currency_code: row.currency_code || "", currency_symbol: row.currency_symbol || "", timezone: row.timezone || "" });
    else setF({ name: "", country: "India", currency_code: "INR", currency_symbol: "₹", timezone: "Asia/Kolkata" });
  }, [row, open]);
  if (!open) return null;
  const save = async () => {
    if (!f.name.trim()) return toast.error("Facility name is required");
    if (!row && !/^[A-Za-z0-9]{20}$/.test((f.ghl_location_id || "").trim())) return toast.error("Enter the gym's 20-character GoHighLevel location ID");
    setBusy(true);
    try {
      if (row) await updateFacility(row.id, f);
      else await createFacility({ ...f, ghl_location_id: f.ghl_location_id.trim() });
      toast.success(row ? "Facility updated" : "Facility created");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="settings-div-1266" className="fixed inset-0 z-40 bg-black/60 grid place-items-center px-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl border border-[#262626] bg-[#0f0f0f] p-5" onClick={(e) => e.stopPropagation()} data-testid="facility-edit-dialog">
        <div className="flex items-center gap-2 mb-3">
          <Building2 className="h-4 w-4 text-blue-400" />
          <h3 className="font-display text-lg font-bold">{row ? "Edit facility" : "New facility"}</h3>
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Name</Label>
            <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="bg-[#0a0a0a] border-[#262626] mt-1" data-testid="facility-name-input" />
          </div>
          {!row && (
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">GoHighLevel location ID</Label>
              <Input value={f.ghl_location_id || ""} onChange={(e) => setF({ ...f, ghl_location_id: e.target.value })}
                placeholder="e.g. kg9psHvpnMAlx6FTMgv5" className="bg-[#0a0a0a] border-[#262626] mt-1 font-mono" data-testid="facility-ghl-location-input" />
              <p className="text-[10px] text-zinc-600 mt-1">The gym&apos;s own GHL sub-account (the code after /location/ in its GHL address).</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Country</Label>
              <Input value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} className="bg-[#0a0a0a] border-[#262626] mt-1" data-testid="facility-country-input" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Timezone (e.g. Asia/Dubai)</Label>
              <Input value={f.timezone} onChange={(e) => setF({ ...f, timezone: e.target.value })} className="bg-[#0a0a0a] border-[#262626] mt-1" data-testid="facility-timezone-input" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Currency code (INR, USD, AED)</Label>
              <Input value={f.currency_code} onChange={(e) => setF({ ...f, currency_code: e.target.value.toUpperCase() })} className="bg-[#0a0a0a] border-[#262626] mt-1" data-testid="facility-currency-code-input" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Currency symbol</Label>
              <Input value={f.currency_symbol} onChange={(e) => setF({ ...f, currency_symbol: e.target.value })} className="bg-[#0a0a0a] border-[#262626] mt-1" data-testid="facility-currency-symbol-input" />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button data-testid="settings-button-1297" variant="outline" onClick={onClose} className="border-[#262626] bg-transparent hover:bg-[#141414]">Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-500 rounded-full" data-testid="facility-save-btn">{busy ? "Saving…" : "Save facility"}</Button>
        </div>
      </div>
    </div>
  );
}

function FacilityUsersDialog({ facility, onClose }) {
  const [data, setData] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteRole, setInviteRole] = useState("user");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (facility) listFacilityUsers(facility.id).then(setData).catch(() => setData(null));
  }, [facility]);
  if (!facility) return null;
  const doInvite = async () => {
    if (nameProblem(inviteName)) return toast.error(`Full name: ${nameProblem(inviteName)}`);
    if (!inviteEmail.trim()) return toast.error("Email required");
    setBusy(true);
    try {
      await import("@/lib/api").then(({ inviteUser }) => inviteUser(inviteEmail.trim().toLowerCase(), inviteRole, facility.id, undefined, autoFixName(inviteName)));
      toast.success(`Invite sent for ${inviteEmail}`);
      setInviteEmail("");
      setInviteName("");
      const fresh = await listFacilityUsers(facility.id);
      setData(fresh);
    } catch (e) { toast.error(e?.response?.data?.detail || "Invite failed"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="settings-div-1327" className="fixed inset-0 z-40 bg-black/60 grid place-items-center px-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-xl border border-[#262626] bg-[#0f0f0f] p-5" onClick={(e) => e.stopPropagation()} data-testid="facility-users-dialog">
        <div className="flex items-center gap-2 mb-4">
          <UserRound className="h-4 w-4 text-blue-400" />
          <h3 className="font-display text-lg font-bold">{facility.name} · users</h3>
        </div>
        <div className="mb-4 rounded-lg border border-[#1f1f1f] bg-[#0a0a0a] p-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Invite by email (invite-only signup)</div>
          <div className="flex items-center gap-2">
            <Input value={inviteName} onChange={(e) => setInviteName(e.target.value)} onBlur={() => inviteName && setInviteName(autoFixName(inviteName))}
              placeholder="Full name" className="bg-[#0d0d0d] border-[#262626] w-44" data-testid="facility-invite-name" />
            <Input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="new-teammate@example.com" className="bg-[#0d0d0d] border-[#262626] flex-1" data-testid="facility-invite-email" />
            <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} className="rounded-md bg-[#0d0d0d] border border-[#262626] text-sm px-2 py-1.5 text-zinc-100" data-testid="facility-invite-role">
              <option value="user">User</option>
              <option value="manager">Manager</option>
            </select>
            <Button onClick={doInvite} disabled={busy} size="sm" className="bg-blue-600 hover:bg-blue-500 rounded-full" data-testid="facility-invite-btn">{busy ? "…" : "Invite"}</Button>
          </div>
          <div className="text-[10px] text-zinc-500 mt-2">On first Google login the user will be auto-attached to this facility.</div>
        </div>
        <div className="max-h-64 overflow-y-auto">
          {data?.users?.length ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 border-b border-[#1f1f1f]">
                  <th className="px-2 py-2 font-medium">Name</th>
                  <th className="px-2 py-2 font-medium">Email</th>
                  <th className="px-2 py-2 font-medium">Role</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {(data.users || []).map((u) => (
                  <tr key={u.user_id} className="border-b border-[#1a1a1a] last:border-0" data-testid={`facility-user-row-${u.user_id}`}>
                    <td className="px-2 py-2">{u.name}</td>
                    <td className="px-2 py-2 text-xs text-zinc-400">{u.email}</td>
                    <td className="px-2 py-2 text-xs"><span className="inline-flex items-center rounded bg-zinc-500/10 border border-zinc-500/20 px-1.5 py-0.5 text-zinc-300 text-[10px]">{u.facility_role}</span>{u.is_global && <span className="ml-1 text-[10px] text-blue-300">global</span>}</td>
                    <td className="px-2 py-2 text-right">
                      {!u.is_global && (
                        <button
                          onClick={async () => {
                            if (!window.confirm(`Remove ${u.name} from ${facility.name}?`)) return;
                            await detachUserFromFacility(facility.id, u.user_id);
                            const fresh = await listFacilityUsers(facility.id);
                            setData(fresh);
                          }}
                          className="text-[11px] text-rose-300 hover:text-rose-200 underline"
                          data-testid={`facility-user-remove-${u.user_id}`}
                        >Remove</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center text-xs text-zinc-500 py-6">No users attached yet.</div>
          )}
        </div>
        <div className="flex justify-end mt-4">
          <Button data-testid="settings-button-1385" onClick={onClose} className="bg-zinc-800 hover:bg-zinc-700 rounded-full">Close</Button>
        </div>
      </div>
    </div>
  );
}
